import {
  IStackItemStyles,
  Stack,
  TextField,
  getTheme,
  Pivot,
  PivotItem,
  IBasePicker,
  ITag,
  Text,
  StackItem,
  DefaultButton,
  DialogFooter,
  Dialog
} from '@fluentui/react';
import { ContentFrames, LabelSelection, SubmitButtons } from 'components/shared';
import { IPivotStyles } from '@fluentui/react';
import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Opinion, Case, OpinionCategoryLabels, QueueConfiguration, ContentType } from 'services';
import { ReviewContext, CaseReview } from './ReviewContextProvider';
import { EvidenceTable, CaseFeedback, traitJoiningString } from 'components/layout';
import { usePrevious } from 'components/util';
import { SalusPrimaryButton } from '../shared/SalusPrimaryButton';
import { TraitLookup } from './TraitLookup';

const feedbackStyle: IStackItemStyles = {
  root: {
    overflowY: 'auto'
  }
};
const applyButtonStyle = { root: { minHeight: 40 } };

interface ReviewOpinionFormProps {
  labelsData: {
    [id: string]: string[];
  };
  labelsPrompt: Record<string, unknown>[];
  config: QueueConfiguration;
  moderatorFeedbackCommentOptional: boolean;
  onSubmitAndNextClick(review: CaseReview[]): void;
  onSubmitAndExitClick(review: CaseReview[]): void;
  disableSubmit: boolean;
}

const maxCommentLength = 500;

function hasValidOpinion(moderatorFeedbackCommentOptional: boolean, opinion?: Opinion): boolean {
  return (
    opinion !== undefined &&
    (moderatorFeedbackCommentOptional || opinion.comment.length !== 0) &&
    opinion.getTraitCount() > 0
  );
}

export function getCaseEvidenceAndContext(selectedCase?: Case): [string, [string, string][], [string, string][]] {
  let evidence: [string, string][] = [];
  let context: [string, string][] = [];

  if (!selectedCase) {
    return ['none', evidence, context];
  }

  // todo: remove these
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  evidence =
    selectedCase.caseEvidence &&
    Object.keys(selectedCase.caseEvidence).map(key => {
      return [key, selectedCase.caseEvidence[key]];
    });
  // @ts-ignore
  context =
    selectedCase.caseContext &&
    Object.keys(selectedCase.caseContext).map(key => {
      return [key, selectedCase.caseContext[key]];
    });
  return [selectedCase.id, evidence, context];
}

function getSelectionHeader(count: number): string {
  if (count === 0) {
    return 'Select a case to review.';
  } else if (count === 1) {
    return 'You have a case selected.';
  } else {
    return `${count} cases selected`;
  }
}

interface DialogData {
  isOpen: boolean;
  selectNextOnClose?: boolean;
}

export const ReviewOpinionForm: React.FunctionComponent<ReviewOpinionFormProps> = props => {
  const theme = getTheme();
  const { state, dispatch } = useContext(ReviewContext);
  const [comment, setComment] = useState<string>('');
  const [dialogData, setDialogData] = useState<DialogData>({ isOpen: false });
  const [traits, setTraits] = useState<OpinionCategoryLabels>({});
  const [tempTraits, setTempTraits] = useState<string[]>([]);
  const [promptTraits, setPromptTraits] = useState<string[]>([]); // this is a list of traits the moderator have selected that will bring up the escalation prompt
  const [iteration, setIteration] = useState<number>(0); // a little worried about this black magic
  const [pivotKey, setPivotKey] = useState<string>('0');
  const formStartRef = useRef<IBasePicker<ITag>>(null);
  const queueData = props.config.categorizedTraitDefinitions;
  const isSubmitEnabled =
    !props.disableSubmit &&
    state.cases.length > 0 &&
    state.cases.every(item => hasValidOpinion(props.moderatorFeedbackCommentOptional, item.opinion));
  const isSingleCase = props.config.defaultMaxAssignedCaseCount === 1;
  const isMultiCase = props.config.defaultMaxAssignedCaseCount > 1;

  // View varies on selected case count: 0, 1, or >1
  const selectedCaseIndex = state.cases.reduce<number[]>(
    (prev, cur, index) => (cur?.isSelected ? [...prev, index] : prev),
    []
  );
  const hasSelection = selectedCaseIndex.length > 0;
  const firstSelectedCase = hasSelection ? state.cases[selectedCaseIndex[0]] : undefined;
  const isSingleSelection = selectedCaseIndex.length === 1;
  const [selectedId, selectedEvidence, selectedContext] = getCaseEvidenceAndContext(
    firstSelectedCase && firstSelectedCase.case
  );
  // 44px is default height of pivot tab
  const pivotStyles: Partial<IPivotStyles> = {
    root: { position: 'relative', minWidth: '350px', marginLeft: 24 },
    itemContainer: { position: 'absolute', height: 'calc(100% - 44px)', width: '100%', overflowY: 'auto' },
    text: {
      fontFamily: theme.fonts.mediumPlus.fontFamily,
      fontSize: theme.fonts.mediumPlus.fontSize
    },
    link: {
      padding: 0,
      marginRight: 16,
      color: !isSingleSelection ? theme.palette.neutralTertiary : theme.palette.black
    },
    linkIsSelected: {
      color: theme.palette.black,
      padding: 0,
      marginRight: 16,
      selectors: { '::before': { width: '100%', left: 0, height: 4, borderRadius: 2 } }
    }
  };

  const resetFocus = (): void => {
    if (formStartRef.current == null) {
      return;
    }
    setTimeout(() => formStartRef && formStartRef.current && formStartRef.current.focusInput(), 5);
  };

  const putOpinionFromState = useCallback((): void => {
    const opinion = new Opinion();
    opinion.comment = comment;
    opinion.opinionCategoryLabels = traits;
    dispatch({ type: 'PUT_OPINION', payload: opinion });
  }, [comment, traits, dispatch]);

  const onMultiCaseApply = (): void => {
    setTempTraits([]);
    putOpinionFromState();
    setComment('');
    setTraits({});
    setIteration(iteration + 1);
    dispatch({ type: 'UNSELECT_ALL' });
  };

  const selectNextItem = (): void => {
    const nextUnreviewedElement = state.cases.find(item => item.opinion === undefined);
    if (nextUnreviewedElement !== undefined) {
      dispatch({ type: 'SELECT_CASE', payload: [nextUnreviewedElement.case] });
      resetFocus();
    }
  };

  const nextUnselectedUnreviewedElement = state.cases.find(item => !item.isSelected && !item.opinion);
  // The goal is to check and see if any of the moderator selected traits match
  // any of the traits in the labelsPrompt list (these are traits that may require legal action),
  // in which case we want a modal to prompt the moderator and make sure they made the right selection.
  const getPromptTraits = (traits?: string[]): string[] => {
    const traitsToUse = traits && traits.length ? traits : tempTraits;

    // Doing a little magic here to make these two data structures comparable.
    const listOfTraitsToPrompt = props.labelsPrompt
      .map(obj => Object.keys(obj)[0]?.toLowerCase())
      .filter(trait => !!trait);
    const loweredTempTraits = traitsToUse.map(tempTrait => tempTrait.split(traitJoiningString)[1].toLowerCase());
    const selectedPromptTraits = listOfTraitsToPrompt.filter(labelPrompt => loweredTempTraits.includes(labelPrompt));
    setPromptTraits(selectedPromptTraits);

    return selectedPromptTraits;
  };

  const applyTraits = (selectNext: boolean, traits?: string[]): void => {
    const selectedPromptTraits = getPromptTraits(traits);

    if (selectedPromptTraits.length) {
      setDialogData({ isOpen: true, selectNextOnClose: selectNext });
    } else {
      if (isMultiCase) {
        onMultiCaseApply();
      } else {
        putOpinionFromState();
      }

      if (selectNext) {
        selectNextItem();
      }
    }
  };

  const onSubmitAndNextClick = (): void => props.onSubmitAndNextClick(state.cases);
  const onSubmitAndExitClick = (): void => props.onSubmitAndExitClick(state.cases);

  // When in single case we don't have an apply button, so we need to check and see if we should show the trait prompt whenever traits change
  useEffect(() => {
    if (isSingleCase) {
      applyTraits(false);
    }
  }, [isSingleCase, traits]);

  // Also single case here, we don't want to show the prompt for every letter typed, but we do want to keep the comment state.cases up to date
  useEffect(() => {
    if (isSingleCase) {
      putOpinionFromState();
    }
  }, [isSingleCase, comment]);

  const previousCaseIndex = usePrevious(selectedCaseIndex);
  useEffect(() => {
    if (
      previousCaseIndex.length === selectedCaseIndex.length &&
      selectedCaseIndex.every((value, index) => value === previousCaseIndex[index])
    ) {
      return;
    }

    if (!isSingleSelection) {
      setPivotKey('0');
    }

    resetFocus();
  }, [selectedCaseIndex, isSingleSelection, previousCaseIndex]);

  const commentText = props.moderatorFeedbackCommentOptional ? 'optional' : 'required';

  // Updating element key resets underlying form elements.
  return (
    <Stack className="h-100">
      <StackItem grow>
        <nav className="h-100 position-relative">
          <Pivot
            className="h-100 position-relative"
            styles={pivotStyles}
            selectedKey={pivotKey}
            onLinkClick={(item): void => {
              if (
                item &&
                item.props.headerButtonProps &&
                !item.props.headerButtonProps['disabled'] &&
                item.props.itemKey
              ) {
                setPivotKey(item.props.itemKey);
              }
            }}
          >
            <PivotItem
              headerText="Actions"
              itemKey="0"
              className="h-100 px-4 pt-2"
              headerButtonProps={{ disabled: false }}
            >
              <Stack className="h-100" key={iteration}>
                <Stack.Item grow className="h-100" styles={feedbackStyle}>
                  <Stack>
                    {!isSingleCase && (
                      <Stack.Item className="my-3">
                        <Text variant="mediumPlus">{getSelectionHeader(selectedCaseIndex.length)}</Text>
                      </Stack.Item>
                    )}
                    <Stack.Item>
                      <label style={{ fontFamily: theme.fonts.mediumPlus.fontFamily }} htmlFor="traitsInput">
                        Traits (minimum 1 label)
                      </label>
                      <LabelSelection
                        focusRef={formStartRef}
                        uxPreferences={props.config.uxPreferences}
                        isDisabled={!hasSelection}
                        onComponentChange={(newOpinion: Opinion): void => {
                          setTraits({ ...newOpinion.opinionCategoryLabels });
                        }}
                        onTraitSelection={(trait: string[]): void => {
                          setTempTraits(trait);
                        }}
                        selectedTrait={tempTraits}
                        labelsData={props.labelsData}
                        renderTraitDefinitions={true}
                      />
                    </Stack.Item>
                    <Stack.Item grow className="mt-4">
                      <TextField
                        label="Comments"
                        styles={{
                          wrapper: {
                            selectors: {
                              label: {
                                fontFamily: theme.fonts.mediumPlus.fontFamily,
                                fontVariant: 'mediumPlus',
                                fontWeight: 'normal'
                              }
                            }
                          }
                        }}
                        rows={5}
                        type="textarea"
                        disabled={!hasSelection}
                        maxLength={maxCommentLength}
                        onChange={(event, value): void => {
                          if (value !== undefined) {
                            setComment(value);
                          }
                        }}
                        multiline
                        resizable={false}
                        autoAdjustHeight
                        placeholder={`Enter moderator notes (${commentText})`}
                      />
                      <Text variant="mediumPlus" style={{ color: theme.palette.neutralSecondary }}>
                        {comment.length}/{maxCommentLength}
                      </Text>
                    </Stack.Item>
                    {isMultiCase ? (
                      <Stack.Item>
                        <SalusPrimaryButton
                          className="my-2 mr-2"
                          styles={applyButtonStyle}
                          disabled={
                            !hasSelection ||
                            !hasValidOpinion(props.moderatorFeedbackCommentOptional, new Opinion(traits, comment))
                          }
                          onClick={(): void => applyTraits(false)}
                          text="Apply"
                        />
                        <DefaultButton
                          className="my-2"
                          styles={applyButtonStyle}
                          disabled={
                            !nextUnselectedUnreviewedElement ||
                            !hasSelection ||
                            !hasValidOpinion(props.moderatorFeedbackCommentOptional, new Opinion(traits, comment))
                          }
                          onClick={(): void => applyTraits(true)}
                          text="Apply & select next"
                        />
                      </Stack.Item>
                    ) : (
                      undefined
                    )}
                  </Stack>
                </Stack.Item>
              </Stack>
            </PivotItem>
            {(props.config.contentType === ContentType.video || props.config.contentType === ContentType.audio) && (
              <PivotItem
                headerText="Frames"
                className="h-100 px-4 pt-2"
                itemKey="1"
                headerButtonProps={{ disabled: !isSingleSelection }}
              >
                {firstSelectedCase && firstSelectedCase.case && firstSelectedCase.case.subject.subjectMetadata && (
                  <ContentFrames metadata={firstSelectedCase.case.subject.subjectMetadata} />
                )}
              </PivotItem>
            )}
            <PivotItem
              headerText="Details"
              className="h-100 px-4 pt-2"
              itemKey="2"
              headerButtonProps={{ disabled: !isSingleSelection }}
            >
              <div>
                <span className="font-weight-bold">CaseID:</span>
                <p className="pb-2">{selectedId}</p>
              </div>
              {selectedEvidence && selectedEvidence.length >= 1 && <EvidenceTable rows={selectedEvidence} />}
              {props.config.uxPreferences.showReportContext && selectedContext && selectedContext.length >= 1 && (
                <EvidenceTable rows={selectedContext} />
              )}
            </PivotItem>
            <PivotItem
              headerText="User reports"
              itemKey="3"
              className="h-100 px-4 pt-2"
              headerButtonProps={{ disabled: !isSingleSelection }}
            >
              {firstSelectedCase && <CaseFeedback feedbacks={firstSelectedCase.feedback} />}
            </PivotItem>
            {!!queueData && state && (
              <PivotItem
                headerText="Trait Lookup"
                itemKey="4"
                className="h-100 px-4 pt-2"
                headerButtonProps={{ disabled: !isSingleSelection }}
              >
                <TraitLookup />
              </PivotItem>
            )}
          </Pivot>
        </nav>
      </StackItem>
      <StackItem>
        <Text variant="mediumPlus">
          <SubmitButtons
            isEnabled={isSubmitEnabled}
            multipleCases={isMultiCase}
            onClickAndNext={onSubmitAndNextClick}
            onClickAndExit={onSubmitAndExitClick}
          />
        </Text>
      </StackItem>

      <Dialog
        hidden={!dialogData.isOpen}
        dialogContentProps={{
          title: `Adding ${promptTraits.join(', ')} will escalate this case.`,
          subText:
            (Object.values((props.labelsPrompt || [])[0] || {})[0] as string) ||
            `Are you sure you want to escalate this case to the Digital Safety Team?`
        }}
        modalProps={{
          overlay: {
            isDarkThemed: true
          }
        }}
      >
        <DialogFooter>
          <SalusPrimaryButton
            onClick={(): void => {
              if (isMultiCase) {
                onMultiCaseApply();
              } else {
                putOpinionFromState();
              }

              if (dialogData.selectNextOnClose) {
                selectNextItem();
              }
              setDialogData({ isOpen: false });
            }}
            text="Ok"
          />
          <DefaultButton
            onClick={(): void => {
              setDialogData({ isOpen: false });

              if (isSingleCase) {
                setTempTraits([]);
                setTraits({});
                setIteration(iteration + 1);
              }
            }}
            text="Cancel"
          />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
};
